/**
 * React Query hook for authentication methods
 *
 * Provides data fetching and mutation for tenant authentication method configurations
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthenticationMethodsApiService } from '../services/authenticationMethodsApi';
import type {
  AuthenticationMethodConfigResponse,
  AuthenticationMethodConfigRequest,
} from '../types/authenticationMethods';

/**
 * Query key factory for authentication methods
 */
export const authenticationMethodsQueryKey = (tenantId: string) =>
  ['tenant', tenantId, 'authenticationMethods'] as const;

/**
 * Update method request with methodKey included
 */
export interface UpdateMethodRequest extends AuthenticationMethodConfigRequest {
  methodKey: string;
}

/**
 * Hook return type for useAuthenticationMethods
 */
export interface UseAuthenticationMethodsResult {
  /** Authentication methods data */
  methods: AuthenticationMethodConfigResponse[] | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Fetching state (true during refetch) */
  isFetching: boolean;
  /** Error from fetching methods */
  error: Error | null;
  /** Function to update an authentication method */
  updateMethod: (request: UpdateMethodRequest) => Promise<AuthenticationMethodConfigResponse>;
  /** Loading state for update mutation */
  isUpdating: boolean;
  /** Error from update mutation */
  updateError: Error | null;
  /** Refetch methods */
  refetch: () => void;
}

/**
 * React Query hook for managing authentication methods
 *
 * @param tenantId - Tenant ID to fetch authentication methods for
 * @returns Authentication methods data, loading states, and mutation functions
 */
export function useAuthenticationMethods(
  tenantId: string
): UseAuthenticationMethodsResult {
  const queryClient = useQueryClient();

  // Query for fetching all authentication methods
  const {
    data: methods,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: authenticationMethodsQueryKey(tenantId),
    queryFn: () => AuthenticationMethodsApiService.getAllMethodConfigurations(tenantId),
  });

  // Mutation for updating an authentication method
  const {
    mutateAsync: updateMethod,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (request: UpdateMethodRequest) =>
      AuthenticationMethodsApiService.updateMethodConfiguration(
        tenantId,
        request.methodKey,
        {
          state: request.state,
          configuration: request.configuration,
        }
      ),
    onSuccess: () => {
      // Invalidate the query to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: authenticationMethodsQueryKey(tenantId),
      });
    },
  });

  return {
    methods,
    isLoading,
    isFetching,
    error: error as Error | null,
    updateMethod,
    isUpdating,
    updateError: updateError as Error | null,
    refetch,
  };
}
